import { useEffect, useRef, useState } from "react";
import "./imageCropper.scss";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import CustomPopup from "../Common/CustomPopup/CustomPopup";
import close from '../../../../assets/images/ads/close_button.svg'

const ImageCropper = ({ onCrop, onClose, cropWidth = 1024, cropHeight = 600 }) => {
    const ASPECT_RATIO = cropWidth / cropHeight;
    const MIN_DIMENSION = 150;
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const [imgSrc, setImgSrc] = useState("");
    const [crop, setCrop] = useState(null);
    const [completedCrop, setCompletedCrop] = useState(null);
    const [fileType, setFileType] = useState("");
    const [error, setError] = useState("");
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const onSelectFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset state and canvas dimensions
        setCrop(null);
        setCompletedCrop(null);
        if (previewCanvasRef.current) {
            const ctx = previewCanvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
        }

        setFileType(file.type);
        e.target.value = "";

        if (file.size > MAX_FILE_SIZE) {
            setError("Image size must not exceed 5 MB.");
            return;
        }

        const reader = new FileReader();
        reader.addEventListener("load", () => {
            const imageElement = new Image();
            const imageUrl = reader.result?.toString() || "";
            imageElement.src = imageUrl;

            imageElement.addEventListener("load", (e) => {
                if (error) setError("");
                const { naturalWidth, naturalHeight } = e.currentTarget;
                if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
                    setError("Image must be at least 150 x 150 pixels.");
                    return;
                }

                setImgSrc(imageUrl);
                setError("");
                setIsPopupOpen(true);
                // fileInputRef.current.value = "";
            });
        });
        reader.readAsDataURL(file);
    };


    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const newCrop = centerCrop(
            makeAspectCrop(
                {
                    unit: "%", // Can be "px" or "%"
                    width: (cropWidth / width) * 100,
                    height: (cropHeight / height) * 100,
                    x: 0,
                    y: 0,
                },
                ASPECT_RATIO,
                width,
                height
            ),
            width,
            height
        );

        // Adjust the y coordinate to 0 to select the top portion
        newCrop.y = 0;
        setCrop(newCrop);
    };

    const handleCropImage = (e) => {
        e.preventDefault();
    
        // Check for necessary references and crop data
        if (!imgRef.current || !previewCanvasRef.current || !completedCrop) {
            setError("Image or canvas reference is unavailable.");
            return;
        }
    
        const canvas = previewCanvasRef.current;
        const ctx = canvas.getContext("2d");
    
        // Reset canvas before redrawing
        canvas.width = 0;
        canvas.height = 0;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    
        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
        // If no crop dimensions, prevent further processing
        if (!completedCrop.width || !completedCrop.height) {
            handleClosePopup();
            setError("Invalid crop dimensions.");
            return;
        }
    
        // Calculate the crop width and height in pixels
        const cropWidthInPixels = completedCrop.width * scaleX;
        const cropHeightInPixels = completedCrop.height * scaleY;
    
        // Set canvas dimensions to match the crop size
        canvas.width = cropWidthInPixels;
        canvas.height = cropHeightInPixels;
    
        // Draw the cropped image onto the canvas
        ctx.drawImage(
            imgRef.current,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            cropWidthInPixels,
            cropHeightInPixels,
            0,
            0,
            canvas.width,
            canvas.height
        );
    
        // Convert canvas to data URL with the specified file type
        const dataUrl = canvas.toDataURL(fileType, fileType === "image/jpeg" ? 0.7 : 1.0);
    
        // Pass the cropped image data to the parent component
        onCrop(dataUrl);
    
        // Close the popup after cropping
        handleClosePopup();
    };
    

    const handleClosePopup = () => {
        setImgSrc("");
        setIsPopupOpen(false);
        if (onClose) onClose();
    };

    return (
        <>
            <label className="file-input-label">
                <input type="file" accept="image/*" onChange={onSelectFile} />
            </label>
            <p className={`crop-error text_sm ${error && 'visible'}`}>{error ? error : ''}</p>

            {isPopupOpen && (
                <CustomPopup className="image-cropper-popup">

                    <div className="cropper-wrapper">
                        {imgSrc && (
                            <>
                                <div className="button-group">
                                    <div className="btn_wrap">
                                        <button onClick={handleCropImage} className="text_md crop-button btn primary">
                                            Crop Image
                                        </button>
                                    </div>

                                </div>
                                <div className="crop-container">
                                    <div className="close_btn_wrapper">
                                        <button className="close_button" onClick={handleClosePopup} >
                                            <img src={close} alt="close icon" />
                                        </button>
                                    </div>
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(newCrop) => setCrop(newCrop)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        aspect={ASPECT_RATIO}
                                        onDragStart={(e) => e.preventDefault()} // Prevent losing focus when clicking outside
                                    >
                                        <img ref={imgRef} src={imgSrc} alt="Crop" onLoad={onImageLoad} />
                                    </ReactCrop>
                                </div>
                            </>
                        )}
                        <canvas ref={previewCanvasRef} style={{ display: "none" }} />
                    </div>
                </CustomPopup>
            )}
        </>
    );
};

export default ImageCropper;
